// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { VariableSchema } from "@/lib/expression-validator";

// Property schema for octo CEL predicates, mirroring the variable models in the backend
// (common/rules/model.py). Roots are the values bound into the octo evaluation context;
// `true` marks an untyped scalar leaf, `"float"` / `"int"` a scalar the backend types as a Python
// `float` / `int`, and a string[] marks an enum leaf whose value set is checked against any literal
// it's compared with. Methods (e.g. `.startsWith`, `.matches`) are intentionally omitted — the
// validator ignores method calls.
//
// Keep the numeric types in sync with model.py: a field annotated `float` (or `float | None`) there
// must be `"float"` here, `int` must be `"int"`. They drive two checks — a decimal compared against a
// whole-number literal, and mixed-type arithmetic, which CEL rejects at evaluation time. Getting one
// wrong produces a bogus warning (or hides a real one), so prefer `true` over a guess.

// Enum value sets. day_of_week/month_of_year are the backend's strftime('%A')/('%B') outputs.
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

// `format` is a preformatted string; every other part is a whole number.
const TIME: VariableSchema = { format: true, hour: "int", minute: "int", second: "int" };
const DATE: VariableSchema = { format: true, day: "int", month: "int", year: "int" };
const DATETIME: VariableSchema = {
  format: true,
  day: "int",
  month: "int",
  year: "int",
  hour: "int",
  minute: "int",
  second: "int",
};

// Task properties. `type` defaults to an unconstrained scalar here; buildOctoRuleSchema() swaps in
// the live task-type enum from the back-office context when it's available.
const TASK_BASE: VariableSchema = {
  id: true,
  type: true,
  aisle: "int",
  age_minutes: "int",
  age_hours: "float",
  creation_time: TIME,
  created_hour: "int",
  due_date: DATE,
  first_detected_at: DATETIME,
  tag_name: true,
  discrepancy_score: "float",
  unpaired_boxes: "int",
  unpaired_products: "int",
  parent_filled_at: DATETIME,
  occupancy_score: "float",
  delisted_since: DATETIME,
  compliance_score: "float",
  num_faceovers: "int",
  num_moves: "int",
  num_removes: "int",
  total_actions: "int",
  execution_date: DATE,
  days_overdue: "int",
  missing_tags: "int",
  extra_tags: "int",
  incorrect_prices: "int",
  broken_tags: "int",
  presentability_score: "float",
  issues_present: "int",
};

const OCTO_RULE_SCHEMA: VariableSchema = {
  chain: { id: true },
  date: {
    format: true,
    current_day: "int",
    current_week: "int",
    current_month: "int",
    current_year: "int",
    day_of_week: WEEKDAYS,
    month_of_year: MONTHS,
  },
  time: { format: true, current_hour: "int", current_minute: "int" },
  store: { id: true, open: true, open_time: TIME, close_time: TIME },
  inventory: {
    soh: "float",
    case_pack: "float",
    shc: "float",
    last_changed_at: DATETIME,
    in_transit: true,
    last_positive_soh_at: DATETIME,
  },
  product: {
    upc: true,
    item_number: true,
    name: true,
    recoupable_value: "float",
    is_bestseller: true,
    department: true,
    velocity: "float",
    priority: true,
    is_dsd: true,
    price: "float",
    is_promo: true,
  },
  planogram: { expected_facings: "int", total_products: "int" },
  task: TASK_BASE,
};

/**
 * Octo predicate schema with `task.type` bound to the live enum of task types (canonical + active
 * custom subtypes) from the back-office `/v1/context`. Pass `context.taskTypes`; when it's absent
 * or empty (context not yet loaded) `task.type` stays an unconstrained scalar so nothing is flagged.
 */
export function buildOctoRuleSchema(taskTypes?: readonly string[]): VariableSchema {
  return {
    ...OCTO_RULE_SCHEMA,
    task: { ...TASK_BASE, type: taskTypes && taskTypes.length > 0 ? taskTypes : true },
  };
}

// Root variable names available to predicates, alphabetical — shown as a reference under the editor.
export const OCTO_RULE_VARIABLES = Object.keys(OCTO_RULE_SCHEMA).sort();
